import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FlashcardService } from '@core/services/flashcard.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { ToastrService } from 'ngx-toastr';
import { TrainFlashcard } from '../../../deck-train.models';
import {
  TRAINING_ANSWER_MAX_LENGTH,
  isTrainingConversationWithinLimits,
} from '../../../training-input-limits';

type ShortTalkMessageRole = 'bot' | 'learner';

type ShortTalkMessage = {
  id: number;
  role: ShortTalkMessageRole;
  text: string;
  audioUrl?: string | null;
  audioMimeType?: string | null;
  audioAvailable?: boolean;
};

export type ShortTalkConversationMessage = {
  role: ShortTalkMessageRole;
  text: string;
};

type SpeechRecognitionResultLike = ArrayLike<{
  transcript: string;
  confidence?: number;
}> & {
  isFinal?: boolean;
};

type SpeechRecognitionEventLike = {
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous?: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

type SpeechRecognitionGlobal = typeof globalThis & {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
};

@Component({
  selector: 'app-short-talk-train-front',
  standalone: true,
  imports: [],
  templateUrl: './short-talk-train-front.html',
  styleUrl: './short-talk-train-front.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortTalkTrainFront implements OnDestroy {
  private static readonly MIN_CONTEXT_MESSAGES = 12;
  private static readonly MAX_CONTEXT_MESSAGES = 24;
  private static readonly SPEECH_INACTIVITY_STOP_MS = 3200;
  private static readonly OPENING_REPLY_PROMPT =
    'Send only your first partner message for this roleplay. Use the target practice language implied by the card when clear. Use at most one short sentence. You may greet, briefly introduce yourself, or ask exactly one simple opening question. Do not write a transcript, do not speak as the learner, do not give feedback, do not ask multiple questions, and do not complete the learner goal.';

  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly flashcardService = inject(FlashcardService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');
  private readonly toastr = inject(ToastrService);
  private recognition: SpeechRecognitionLike | null = null;
  private speechInactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private messageId = 0;
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollAnimationFrame: number | null = null;
  private hasShownTurnLimitToast = false;
  private currentBotAudio: HTMLAudioElement | null = null;
  private currentConversationFlashcardId: number | null = null;

  readonly card = input.required<TrainFlashcard>();
  readonly shortTalkSessionId = input<number | null>(null);
  readonly openingReplyRequestId = input(0);
  readonly canSpeakQuestion = input(false);
  readonly isQuestionAudioPlaying = input(false);
  readonly speakQuestion = output<void>();
  readonly stopPlayback = output<void>();
  readonly introStarted = output<void>();
  readonly messagesChange = output<ShortTalkConversationMessage[]>();
  readonly messagesContainer =
    viewChild<ElementRef<HTMLElement>>('messagesContainer');
  readonly messageInput = viewChild<ElementRef<HTMLElement>>('messageInput');
  readonly draftInput = viewChild<ElementRef<HTMLInputElement>>('draftInput');
  readonly isIntroVisible = signal(true);
  readonly messages = signal<ShortTalkMessage[]>([]);
  readonly draftMessage = signal('');
  readonly isListening = signal(false);
  readonly isSending = signal(false);
  readonly isSpeakingBotMessage = signal(false);
  readonly speakingBotMessageId = signal<number | null>(null);
  private readonly startedIntroFlashcardIds = new Set<number>();
  private readonly handledOpeningReplyRequestIds = new Set<number>();

  readonly t = this.deckTranslations.t;
  readonly maxMessageLength = TRAINING_ANSWER_MAX_LENGTH;
  readonly maxTurns = computed(() => {
    const rawMaxTurns = this.card().shortTalkMaxTurns;
    return rawMaxTurns >= 5 && rawMaxTurns <= 50 ? rawMaxTurns : 10;
  });
  readonly learnerTurnCount = computed(
    () =>
      this.messages().filter((message) => message.role === 'learner').length,
  );
  readonly isTurnLimitReached = computed(
    () => this.learnerTurnCount() >= this.maxTurns(),
  );
  readonly canUseSpeechRecognition = computed(
    () => this.resolveSpeechRecognitionCtor() !== null,
  );
  readonly inputLang = computed(() =>
    this.resolveInputLanguage(this.card().languageCodeProvider),
  );
  readonly inputSpellcheckEnabled = computed(() => this.inputLang() !== null);
  readonly maxContextMessages = computed(() =>
    Math.min(
      Math.max(
        this.maxTurns() * 2 + 1,
        ShortTalkTrainFront.MIN_CONTEXT_MESSAGES,
      ),
      ShortTalkTrainFront.MAX_CONTEXT_MESSAGES,
    ),
  );

  constructor() {
    effect(() => {
      const card = this.card();
      if (this.currentConversationFlashcardId === card.id) {
        return;
      }

      this.currentConversationFlashcardId = card.id;
      this.resetConversation();
    });

    effect(() => {
      const requestId = this.openingReplyRequestId();
      if (requestId <= 0 || this.handledOpeningReplyRequestIds.has(requestId)) {
        return;
      }

      this.handledOpeningReplyRequestIds.add(requestId);
      this.requestOpeningReply();
    });
  }

  get promptText(): string {
    const card = this.card();
    return card.shortTalkLearnerGoal || card.frontText;
  }

  get scenarioText(): string {
    return this.card().shortTalkScenario;
  }

  get introGoalText(): string {
    return this.promptText || this.t()['train.noQuestionText'];
  }

  get speechButtonTitle(): string {
    if (!this.canUseSpeechRecognition()) {
      return this.t()['train.shortTalkSpeechUnavailable'];
    }

    return this.isListening()
      ? this.t()['train.shortTalkListening']
      : this.t()['train.microphone'];
  }

  ngOnDestroy(): void {
    this.clearScheduledScroll();
    this.stopSpeechRecognition();
    this.stopCurrentBotAudioPlayback();
  }

  onDraftInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement | null;
    this.draftMessage.set(inputElement?.value ?? '');
  }

  sendDraftMessage(): void {
    const text = this.draftMessage();
    this.sendMessage(text);
  }

  startConversation(): void {
    if (!this.isIntroVisible()) {
      return;
    }

    this.startedIntroFlashcardIds.add(this.card().id);
    this.isIntroVisible.set(false);
    this.introStarted.emit();
    this.scheduleScrollToInput({ focusInput: true });
  }

  speakLastBotMessage(): void {
    const lastBotMessage = [...this.messages()]
      .reverse()
      .find(
        (message) =>
          message.role === 'bot' &&
          message.audioAvailable === true &&
          !!message.audioUrl,
      );
    if (!lastBotMessage) {
      return;
    }

    this.speakBotMessage(lastBotMessage);
  }

  speakBotMessage(message: ShortTalkMessage): void {
    if (
      message.role !== 'bot' ||
      !message.audioAvailable ||
      !message.audioUrl
    ) {
      return;
    }

    const startedAt = this.nowInMilliseconds();
    this.stopCurrentBotAudioPlayback();

    const audio = new Audio(message.audioUrl);
    audio.preload = 'auto';
    this.currentBotAudio = audio;
    this.isSpeakingBotMessage.set(true);
    this.speakingBotMessageId.set(message.id);

    const clearPlaybackState = (): void => {
      audio.onended = null;
      audio.onerror = null;

      if (this.currentBotAudio === audio) {
        this.currentBotAudio = null;
      }

      if (this.speakingBotMessageId() === message.id) {
        this.speakingBotMessageId.set(null);
      }

      this.isSpeakingBotMessage.set(false);
      this.cdr.markForCheck();
    };

    audio.onended = () => {
      this.logShortTalkTiming('tts.success', startedAt, {
        flashcardId: this.card().id,
        replyLength: message.text.length,
      });
      clearPlaybackState();
    };

    audio.onerror = () => {
      this.logShortTalkTiming('tts.error', startedAt, {
        flashcardId: this.card().id,
        replyLength: message.text.length,
      });
      clearPlaybackState();
      this.toastr.error(
        this.t()['train.audioUnavailable'],
        this.t()['train.shortTalkTitle'],
      );
    };

    void audio.play().catch(() => {
      this.logShortTalkTiming('tts.error', startedAt, {
        flashcardId: this.card().id,
        replyLength: message.text.length,
      });
      clearPlaybackState();
      this.toastr.error(
        this.t()['train.audioUnavailable'],
        this.t()['train.shortTalkTitle'],
      );
    });
  }

  isBotMessageBusy(messageId: number): boolean {
    return (
      this.isSpeakingBotMessage() && this.speakingBotMessageId() === messageId
    );
  }

  isBotMessagePlayable(message: ShortTalkMessage): boolean {
    return (
      message.role === 'bot' &&
      message.audioAvailable === true &&
      !!message.audioUrl
    );
  }

  toggleSpeechRecognition(): void {
    if (!this.canUseSpeechRecognition()) {
      return;
    }

    if (this.isListening()) {
      this.stopSpeechRecognition();
      return;
    }

    const RecognitionCtor = this.resolveSpeechRecognitionCtor();
    if (!RecognitionCtor) {
      return;
    }

    const recognition = new RecognitionCtor();
    recognition.lang = this.resolveSpeechLanguage() || 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 3;
    recognition.onresult = (event) => this.applySpeechResult(event);
    recognition.onerror = () => this.stopSpeechRecognition(false);
    recognition.onend = () => this.stopSpeechRecognition(false);
    this.recognition = recognition;
    this.isListening.set(true);
    this.scheduleSpeechInactivityStop();
    recognition.start();
  }

  private resetConversation(): void {
    const card = this.card();
    this.messageId = 0;
    this.hasShownTurnLimitToast = false;
    this.stopSpeechRecognition();
    this.stopCurrentBotAudioPlayback();
    this.isSending.set(false);
    this.isSpeakingBotMessage.set(false);
    this.speakingBotMessageId.set(null);
    this.draftMessage.set('');
    this.messages.set([]);
    this.emitMessagesChange();
    this.isIntroVisible.set(!this.startedIntroFlashcardIds.has(card.id));
    if (!this.isIntroVisible()) {
      this.scheduleScrollToInput();
    }
  }

  private addCompletionReply(): void {
    const text = this.buildCompletionMessage();
    // The completion notice must not make an otherwise valid review too large.
    if (
      isTrainingConversationWithinLimits(
        [...this.messages(), { role: 'bot', text }],
        100,
      )
    ) {
      this.addMessage('bot', text, {
        audioAvailable: false,
        audioUrl: null,
        audioMimeType: null,
      });
    }
    this.showTurnLimitToast();
  }

  private requestOpeningReply(): void {
    if (
      this.isIntroVisible() ||
      this.messages().length > 0 ||
      this.isSending()
    ) {
      return;
    }

    this.stopPlayback.emit();
    this.stopCurrentBotAudioPlayback();
    this.requestBotReply(ShortTalkTrainFront.OPENING_REPLY_PROMPT, []);
    this.scheduleScrollToInput();
  }

  private requestBotReply(
    learnerMessage: string,
    conversationHistory: ShortTalkMessage[],
  ): void {
    const card = this.card();
    const requestStartedAt = this.nowInMilliseconds();
    const requestMessages = this.buildRequestMessages(conversationHistory);
    if (
      !isTrainingConversationWithinLimits(requestMessages, 50, learnerMessage)
    ) {
      this.toastr.warning(this.t()['train.conversationTooLong']);
      return;
    }
    const sessionId = this.shortTalkSessionId();
    if (!sessionId || sessionId <= 0) {
      this.addMessage('bot', this.buildFollowUpMessage(), {
        audioAvailable: false,
        audioUrl: null,
        audioMimeType: null,
      });
      this.toastr.error(
        this.t()['train.shortTalkReplyError'],
        this.t()['train.shortTalkTitle'],
      );
      this.scheduleScrollToInput();
      return;
    }

    const payload = {
      flashcardId: card.id,
      sessionId,
      learnerMessage,
      messages: requestMessages.map((message) => ({
        role: message.role,
        text: message.text,
      })),
    };

    this.isSending.set(true);
    this.flashcardService.replyShortTalk(card.id, payload).subscribe({
      next: (response) => {
        if (this.card().id !== card.id) {
          return;
        }

        this.applyUiUpdate(() => {
          this.isSending.set(false);
          const reply = response.reply.trim()
            ? response.reply
            : this.buildFollowUpMessage();
          this.logShortTalkTiming('reply.success', requestStartedAt, {
            flashcardId: card.id,
            payloadMessageCount: payload.messages.length,
            replyLength: reply.length,
          });
          const botMessage = this.addMessage('bot', reply, {
            audioAvailable: response.audioAvailable,
            audioUrl: response.audioUrl,
            audioMimeType: response.audioMimeType,
          });

          if (botMessage && this.isBotMessagePlayable(botMessage)) {
            this.speakBotMessage(botMessage);
          }

          this.scheduleScrollToInput();
        });
      },
      error: () => {
        if (this.card().id !== card.id) {
          return;
        }

        this.applyUiUpdate(() => {
          this.isSending.set(false);
          this.logShortTalkTiming('reply.error', requestStartedAt, {
            flashcardId: card.id,
            payloadMessageCount: payload.messages.length,
          });
          this.addMessage('bot', this.buildFollowUpMessage(), {
            audioAvailable: false,
            audioUrl: null,
            audioMimeType: null,
          });
          this.toastr.error(
            this.t()['train.shortTalkReplyError'],
            this.t()['train.shortTalkTitle'],
          );
          this.scheduleScrollToInput();
        });
      },
    });
  }

  private showTurnLimitToast(): void {
    if (this.hasShownTurnLimitToast) {
      return;
    }

    this.hasShownTurnLimitToast = true;
    this.toastr.warning(
      this.t()['train.shortTalkMaxTurnsReachedMessage'],
      this.t()['train.shortTalkMaxTurnsReachedTitle'],
    );
  }

  private buildFollowUpMessage(): string {
    const card = this.card();
    if (card.shortTalkSuccessCriteria) {
      return `${this.t()['train.shortTalkBotReply']} ${
        card.shortTalkSuccessCriteria
      }`;
    }

    return this.t()['train.shortTalkDefaultReply'];
  }

  private buildCompletionMessage(): string {
    const criteria = this.card().shortTalkSuccessCriteria;
    return criteria
      ? `${this.t()['train.shortTalkComplete']} ${criteria}`
      : this.t()['train.shortTalkDefaultComplete'];
  }

  private addMessage(
    role: ShortTalkMessageRole,
    text: string,
    options?: {
      audioUrl?: string | null;
      audioMimeType?: string | null;
      audioAvailable?: boolean;
    },
  ): ShortTalkMessage | null {
    if (
      !isTrainingConversationWithinLimits(
        [...this.messages(), { role, text }],
        100,
      )
    ) {
      this.toastr.warning(this.t()['train.conversationTooLong']);
      return null;
    }
    const message: ShortTalkMessage = {
      id: this.nextMessageId(),
      role,
      text: text.trim(),
      audioUrl: options?.audioUrl ?? null,
      audioMimeType: options?.audioMimeType ?? null,
      audioAvailable: options?.audioAvailable ?? false,
    };
    this.messages.update((messages) => [...messages, message]);
    this.emitMessagesChange();
    this.cdr.markForCheck();
    return message;
  }

  private sendMessage(text: string): void {
    if (text.length > this.maxMessageLength) {
      this.toastr.warning(this.t()['train.answerTooLong']);
      return;
    }
    if (
      !isTrainingConversationWithinLimits(this.messages(), 99, text) ||
      !isTrainingConversationWithinLimits(
        this.buildRequestMessages(this.messages()),
        50,
        text,
      )
    ) {
      this.toastr.warning(this.t()['train.conversationTooLong']);
      return;
    }
    const normalizedText = text.trim();
    if (!normalizedText || this.isTurnLimitReached() || this.isSending()) {
      return;
    }

    this.stopPlayback.emit();
    this.stopCurrentBotAudioPlayback();

    const conversationHistory = this.messages();
    this.draftMessage.set('');
    this.addMessage('learner', normalizedText);
    this.cdr.markForCheck();

    if (this.isTurnLimitReached()) {
      this.addCompletionReply();
      this.scheduleScrollToInput();
      return;
    }

    this.requestBotReply(normalizedText, conversationHistory);
    this.scheduleScrollToInput();
  }

  private buildRequestMessages(
    messages: ShortTalkMessage[],
  ): ShortTalkMessage[] {
    if (messages.length <= this.maxContextMessages()) {
      return messages;
    }
    return messages.slice(-this.maxContextMessages());
  }

  private emitMessagesChange(): void {
    this.messagesChange.emit(
      this.messages().map((message) => ({
        role: message.role,
        text: message.text,
      })),
    );
  }

  private nowInMilliseconds(): number {
    if (typeof globalThis.performance?.now === 'function') {
      return globalThis.performance.now();
    }

    return Date.now();
  }

  private logShortTalkTiming(
    eventName: string,
    startedAt: number,
    details: Record<string, number>,
  ): void {
    if (!this.isTimingLogEnabled()) {
      return;
    }

    const elapsedMs = Math.max(
      0,
      Math.round(this.nowInMilliseconds() - startedAt),
    );

    console.debug('[ShortTalkTrainFront]', {
      event: eventName,
      elapsedMs,
      ...details,
    });
  }

  private isTimingLogEnabled(): boolean {
    try {
      return globalThis.localStorage?.getItem('mindme:short-talk-perf') === '1';
    } catch {
      return false;
    }
  }

  private applyUiUpdate(update: () => void): void {
    this.ngZone.run(() => {
      update();
      this.cdr.markForCheck();
    });
  }

  private applySpeechResult(event: SpeechRecognitionEventLike): void {
    const transcript = this.resolveBestTranscript(event);
    this.scheduleSpeechInactivityStop();

    if (!transcript) {
      return;
    }

    const nextDraft = this.buildDraftFromSpeechTranscript(
      this.draftMessage(),
      transcript,
    );
    if (nextDraft.length > this.maxMessageLength) {
      this.stopSpeechRecognition();
      this.toastr.warning(this.t()['train.answerTooLong']);
      return;
    }
    this.draftMessage.set(nextDraft);
    this.scheduleScrollToInput({ focusInput: true });
  }

  private resolveBestTranscript(event: SpeechRecognitionEventLike): string {
    const latestResult = event.results[event.results.length - 1];
    if (!latestResult) {
      return '';
    }

    let bestTranscript = '';
    let bestConfidence = -1;

    for (let index = 0; index < latestResult.length; index += 1) {
      const alternative = latestResult[index];
      if (!alternative) {
        continue;
      }

      const candidate = this.normalizeSpeechText(alternative.transcript);
      if (!candidate) {
        continue;
      }

      const confidence =
        typeof alternative.confidence === 'number'
          ? alternative.confidence
          : -1;
      if (
        confidence > bestConfidence ||
        (confidence === bestConfidence &&
          candidate.length > bestTranscript.length)
      ) {
        bestConfidence = confidence;
        bestTranscript = candidate;
      }
    }

    if (bestTranscript) {
      return bestTranscript;
    }

    for (let index = 0; index < latestResult.length; index += 1) {
      const candidate = this.normalizeSpeechText(
        latestResult[index]?.transcript,
      );
      if (candidate) {
        return candidate;
      }
    }

    return '';
  }

  private buildDraftFromSpeechTranscript(
    currentDraft: string,
    transcript: string,
  ): string {
    const normalizedDraft = this.normalizeSpeechText(currentDraft);
    if (!normalizedDraft) {
      return transcript;
    }

    return `${normalizedDraft} ${transcript}`;
  }

  private normalizeSpeechText(text: string | undefined): string {
    if (!text) {
      return '';
    }

    return text.replace(/\s+/g, ' ').trim();
  }

  private scheduleScrollToInput(options: { focusInput?: boolean } = {}): void {
    this.clearScheduledScroll();

    this.scrollTimer = setTimeout(() => {
      this.scrollTimer = null;
      if (typeof globalThis.requestAnimationFrame !== 'function') {
        this.scrollToInput(options);
        return;
      }

      this.scrollAnimationFrame = globalThis.requestAnimationFrame(() => {
        this.scrollAnimationFrame = null;
        this.scrollToInput(options);
      });
    });
  }

  private scrollToInput(options: { focusInput?: boolean }): void {
    const messagesElement = this.messagesContainer()?.nativeElement;
    if (messagesElement) {
      messagesElement.scrollTop = messagesElement.scrollHeight;
    }

    const didScroll =
      this.scrollContinueButtonIntoView() ||
      this.scrollInputIntoView() ||
      this.scrollNearestScrollableContainerToBottom();

    if (!didScroll) {
      this.scrollPageToBottom();
    }

    if (options.focusInput) {
      this.draftInput()?.nativeElement.focus({ preventScroll: true });
    }
  }

  private clearScheduledScroll(): void {
    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }

    if (
      this.scrollAnimationFrame !== null &&
      typeof globalThis.cancelAnimationFrame === 'function'
    ) {
      globalThis.cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = null;
    }
  }

  private scrollContinueButtonIntoView(): boolean {
    const shellElement = this.messageInput()?.nativeElement.closest(
      'app-train-card-shell',
    );
    const continueButton = shellElement?.querySelector<HTMLElement>(
      '.train-card-continue',
    );

    if (!continueButton) {
      return false;
    }

    continueButton.scrollIntoView({
      block: 'end',
      inline: 'nearest',
      behavior: 'smooth',
    });
    return true;
  }

  private scrollInputIntoView(): boolean {
    const inputElement = this.messageInput()?.nativeElement;
    if (!inputElement) {
      return false;
    }

    inputElement.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'auto',
    });
    return true;
  }

  private scrollNearestScrollableContainerToBottom(): boolean {
    let element: HTMLElement | null =
      this.messageInput()?.nativeElement ?? this.hostElement.nativeElement;

    while (element) {
      if (this.canScrollVertically(element)) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'auto',
        });
        return true;
      }

      element = element.parentElement;
    }

    return false;
  }

  private canScrollVertically(element: HTMLElement): boolean {
    if (element.scrollHeight <= element.clientHeight) {
      return false;
    }

    const overflowY = globalThis.getComputedStyle(element).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  }

  private scrollPageToBottom(): void {
    const scrollElement = globalThis.document?.scrollingElement;
    const scrollHeight =
      scrollElement?.scrollHeight ??
      globalThis.document?.documentElement?.scrollHeight ??
      0;

    if (scrollHeight <= 0) {
      return;
    }

    globalThis.scrollTo({
      top: scrollHeight,
      behavior: 'auto',
    });
  }

  private nextMessageId(): number {
    this.messageId += 1;
    return this.messageId;
  }

  private resolveSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
    const speechGlobal = globalThis as SpeechRecognitionGlobal;
    return (
      speechGlobal.SpeechRecognition ??
      speechGlobal.webkitSpeechRecognition ??
      null
    );
  }

  private resolveSpeechLanguage(): string {
    const card = this.card();
    return card.languageCodeProvider || 'en-US';
  }

  private resolveInputLanguage(
    languageCodeProvider: string | null | undefined,
  ): string | null {
    const candidate = (languageCodeProvider ?? '').trim().replace(/_/g, '-');
    if (!candidate) {
      return null;
    }

    if (!/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(candidate)) {
      return null;
    }

    const segments = candidate.split('-');
    return segments
      .map((segment, index) => {
        if (index === 0) {
          return segment.toLowerCase();
        }

        if (segment.length === 2 && /^[A-Za-z]{2}$/.test(segment)) {
          return segment.toUpperCase();
        }

        if (segment.length === 4 && /^[A-Za-z]{4}$/.test(segment)) {
          return `${segment[0].toUpperCase()}${segment.slice(1).toLowerCase()}`;
        }

        return segment.toLowerCase();
      })
      .join('-');
  }

  private stopCurrentBotAudioPlayback(): void {
    if (!this.currentBotAudio) {
      return;
    }

    this.currentBotAudio.pause();
    this.currentBotAudio.currentTime = 0;
    this.currentBotAudio.onended = null;
    this.currentBotAudio.onerror = null;
    this.currentBotAudio = null;
    this.isSpeakingBotMessage.set(false);
    this.speakingBotMessageId.set(null);
  }

  private stopSpeechRecognition(shouldStopRecognition = true): void {
    this.clearSpeechInactivityTimer();
    const recognition = this.recognition;
    this.recognition = null;

    if (shouldStopRecognition) {
      recognition?.stop();
    }

    this.isListening.set(false);
  }

  private scheduleSpeechInactivityStop(): void {
    this.clearSpeechInactivityTimer();
    this.speechInactivityTimer = setTimeout(() => {
      this.speechInactivityTimer = null;
      this.stopSpeechRecognition();
    }, ShortTalkTrainFront.SPEECH_INACTIVITY_STOP_MS);
  }

  private clearSpeechInactivityTimer(): void {
    if (this.speechInactivityTimer === null) {
      return;
    }

    clearTimeout(this.speechInactivityTimer);
    this.speechInactivityTimer = null;
  }
}
